import React, { useState, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { api } from '../services/api';
import { formatDateTime } from '../utils/dateUtils';

const Referrals = () => {
    const [referrals, setReferrals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    useEffect(() => {
        fetchReferrals();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    const fetchReferrals = async () => {
        setLoading(true);
        try {
            const params = {
                page: currentPage,
                limit: 20
            };

            const queryString = new URLSearchParams(params).toString();
            const data = await api.get(`/api/vendors/referrals/?${queryString}`);
            
            setReferrals(data.items || []);
            setTotalPages(data.pages || 1);
            setTotalItems(data.total || 0);
        } catch (error) {
            console.error('Error fetching referrals:', error);
            setReferrals([]);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return formatDateTime(dateString);
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center rounded-lg py-0 px-3 ">
                <h1 className="text-2xl font-semibold text-gray-900">Referrals Management</h1>
                <div className="flex gap-2">
                    <button
                        onClick={fetchReferrals}
                        disabled={loading}
                        className="bg-white text-black px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                        <RefreshCw className={`w-4 h-4  ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            <div className=" p-6 bg-white mrgall rounded-lg bxshadowhd border">
                {loading ? (
                    <LoadingSpinner />
                ) : (
                    <>
                        <div className="overflow-x-auto border rounded-md overflowauto-referrals">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-4 text-left whitespace-nowrap text-1xl font-medium text-black-500 ">
                                            Referral Name
                                        </th>
                                        <th className="px-6 py-4 text-left whitespace-nowrap text-1xl font-medium text-black-500 ">
                                            Mobile
                                        </th>
                                        <th className="px-6 py-4 text-left whitespace-nowrap text-1xl font-medium text-black-500">
                                            Location
                                        </th>
                                        <th className="px-6 py-4 text-left whitespace-nowrap text-1xl font-medium text-black-500">
                                            State
                                        </th>
                                        <th className="px-6 py-3 text-left whitespace-nowrap text-1xl font-medium text-black-500">
                                            Referred By
                                        </th>
                                        <th className="px-6 py-4 text-left whitespace-nowrap text-1xl font-medium text-black-500">
                                            Notes
                                        </th>
                                        <th className="px-6 py-4 text-left whitespace-nowrap text-1xl font-medium text-black-500">
                                            Created At
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {referrals.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="px-6 py-4 text-center text-gray-600">
                                                No referrals found
                                            </td>
                                        </tr>
                                    ) : (
                                        referrals.map((referral) => (
                                            <tr key={referral.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-3 whitespace-nowrap">
                                                    <div className="text-sm text-gray-600">
                                                        {referral.referral_name}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-3 whitespace-nowrap">
                                                    <div className="text-sm text-gray-600">
                                                        {referral.referral_mobile}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-3">
                                                    <div className="text-sm text-gray-600 max-w-xs truncate">
                                                        {referral.referral_location}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-3 whitespace-nowrap">
                                                    <div className="text-sm text-gray-600">
                                                        {referral.state || '-'}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-600">
                                                        {referral.referred_by_user_name}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-3">
                                                    <div className="text-sm text-gray-600 max-w-xs truncate">
                                                        {referral.notes || '-'}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-3 whitespace-nowrap">
                                                    <div className="text-sm text-gray-600">
                                                        {formatDate(referral.created_at)}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="p-4 bg-gray-50 rounded-md mt-4 flex justify-between items-center">
                                <div className="text-sm text-gray-700">
                                    Showing {((currentPage - 1) * 20) + 1} to {Math.min(currentPage * 20, totalItems)} of {totalItems} referrals
                                </div>
                                <nav className="flex gap-2">
                                    <button
                                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                        disabled={currentPage === 1}
                                        className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        Previous
                                    </button>
                                    <span className="px-3 py-1">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <button
                                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                        disabled={currentPage === totalPages}
                                        className="px-3 py-1 bg-white border rounded hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        Next
                                    </button>
                                </nav>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default Referrals; 